// ============================================
// RÉFÉRENTIEL — échelle FOURCHETTE (PAMI, plan §5.2)
// ============================================
// Module PUR. Le référentiel donne un intervalle cible [min, max] par dimension ;
// le candidat a un score s. L'écart est la distance à l'intervalle.
//
// UNE QUESTION SAISIE, PAS DE BORNES À LA MAIN. Pour chaque dimension, le
// référent choisit son ATTENTE parmi quatre réponses. Sur l'échelle 0-10 :
//
//     score bas                      [0, 3]
//     score moyen                    [4, 6]
//     score élevé                    [7, 10]
//     pas d'attente particulière     hors calcul
//
// Toutes les dimensions retenues pèsent pareil dans le score.
//
// FORMES HISTORIQUES (lecture seule) : avant l'attente unique, le référent
// répondait à deux questions, IMPORTANCE (très important / important / pas
// important) puis NIVEAU (bas / haut). L'importance réglait la largeur de la
// zone et le poids. Ces contributions restent relisibles avec leurs zones et
// leurs poids d'origine (`intervalleDuNiveau`, `POIDS_IMPORTANCE`).

/** Bornes de l'échelle de saisie (0-10 pour PAMI). */
export type Echelle = { min: number; max: number };

/** Attente du référent pour une dimension — la seule question posée. */
export type AttenteDimension = "bas" | "moyen" | "haut" | "aucune";

/** Attente qui définit une zone : tout sauf « pas d'attente particulière ». */
export type AttenteAvecZone = Exclude<AttenteDimension, "aucune">;

// Ordre de l'écran de saisie : l'échelle monte de gauche à droite, « pas
// d'attente » ferme la liste. Aucun autre usage ne dépend de l'ordre
// (validation par `includes`).
export const ATTENTES: AttenteDimension[] = ["bas", "moyen", "haut", "aucune"];

export function estAttente(valeur: unknown): valeur is AttenteDimension {
  return ATTENTES.includes(valeur as AttenteDimension);
}

export const LIBELLE_ATTENTE: Record<AttenteDimension, string> = {
  bas: "Score bas",
  moyen: "Score moyen",
  haut: "Score élevé",
  aucune: "Pas d'attente particulière",
};

/**
 * Zone cible d'une attente, exprimée en fractions de l'amplitude pour rester
 * correcte si l'échelle change un jour. Sur 0-10 (le seul cas réel) :
 * bas → [0,3], moyen → [4,6], élevé → [7,10].
 */
export function intervalleDeAttente(
  attente: AttenteAvecZone,
  echelle: Echelle,
): { min: number; max: number } {
  const amplitude = echelle.max - echelle.min;
  const cran = (fraction: number) =>
    echelle.min + Math.round(fraction * amplitude);
  if (attente === "bas") return { min: echelle.min, max: cran(0.3) };
  if (attente === "moyen") return { min: cran(0.4), max: cran(0.6) };
  return { min: cran(0.7), max: echelle.max };
}

// ---------- Formes historiques (importance × niveau), lecture seule ----------

/** Niveau historique : la direction de la zone, avant l'attente unique. */
export type NiveauAttendu = "bas" | "haut";

/** Niveau « moyen » d'avant la direction haut/bas, déjà historique alors. */
export type NiveauHistorique = "moyen";

/** Importance historique : réglait la largeur de la zone et le poids. */
export type ImportanceDimension = "forte" | "moyenne" | "faible";

/** Importance qui demandait une zone : tout sauf « pas important ». */
export type ImportanceRetenue = Exclude<ImportanceDimension, "faible">;

const NIVEAUX_HISTORIQUES: readonly string[] = ["bas", "haut", "moyen"];
const IMPORTANCES_HISTORIQUES: readonly string[] = [
  "faible",
  "moyenne",
  "forte",
];

/** Niveau relisible dans une contribution historique. */
export function estNiveauLisible(
  valeur: unknown,
): valeur is NiveauAttendu | NiveauHistorique {
  return NIVEAUX_HISTORIQUES.includes(valeur as string);
}

export function estImportance(valeur: unknown): valeur is ImportanceDimension {
  return IMPORTANCES_HISTORIQUES.includes(valeur as string);
}

/**
 * Poids historique d'une dimension dans le score : 1 (très important) / 0,5
 * (important) / 0 (pas important). Les contributions à attente unique n'en
 * portent pas : elles pèsent `POIDS_PAR_DEFAUT`.
 */
export const POIDS_IMPORTANCE: Record<ImportanceDimension, number> = {
  forte: 1,
  moyenne: 0.5,
  faible: 0,
};

export const POIDS_PAR_DEFAUT = 1;

/**
 * Zone historique d'un niveau pour une importance donnée. Sur 0-10 :
 *
 *   très important : bas → [0,3]   haut → [7,10]
 *   important      : bas → [0,6]   haut → [4,10]
 *
 * Le `moyen` ignore l'importance : [4,6].
 */
export function intervalleDuNiveau(
  niveau: NiveauAttendu | NiveauHistorique,
  importance: ImportanceRetenue,
  echelle: Echelle,
): { min: number; max: number } {
  const amplitude = echelle.max - echelle.min;
  const cran = (fraction: number) =>
    echelle.min + Math.round(fraction * amplitude);
  if (niveau === "moyen") return { min: cran(0.4), max: cran(0.6) };
  const exigeant = importance === "forte";
  if (niveau === "bas")
    return { min: echelle.min, max: cran(exigeant ? 0.3 : 0.6) };
  return { min: cran(exigeant ? 0.7 : 0.4), max: echelle.max };
}

/** Zone en mots : « 7 à 10 », « 0 à 3 ». */
export function libelleZone(cible: { min: number; max: number }): string {
  return `${cible.min} à ${cible.max}`;
}

/**
 * Répartition de la population de référence PAMI dans les 11 classes de
 * l'étalonnage (scores 0 à 10), en %. Mêmes valeurs que la figure « Scores
 * étalonnés en 11 classes » du rapport PAMI : l'écran de saisie la reprend pour
 * montrer au référent la part de la population que couvre sa zone cible.
 */
export const REPARTITION_CLASSES_PAMI = [
  3.6, 4.5, 7.8, 11.5, 14.7, 15.8, 14.7, 11.5, 7.8, 4.5, 3.6,
] as const;

/**
 * Part de la population de référence (en %, au dixième) dont le score tombe
 * dans la zone : somme des classes entières comprises entre ses bornes. Ne
 * vaut que sur l'échelle 0-10 de l'étalonnage.
 */
export function partPopulationZone(cible: { min: number; max: number }): number {
  const somme = REPARTITION_CLASSES_PAMI.reduce(
    (total, part, classe) =>
      classe >= cible.min && classe <= cible.max ? total + part : total,
    0,
  );
  return Math.round(somme * 10) / 10;
}

/**
 * Cible d'une dimension : un intervalle (pondéré pour les contributions
 * historiques), ou l'absence d'attente. « Pas d'attente particulière » est lu
 * comme `nonConcerne` : la dimension sort du calcul.
 */
export type CibleFourchette =
  | { min: number; max: number; poids?: number }
  | { nonConcerne: true };

export function estNonConcerne(
  cible: CibleFourchette,
): cible is { nonConcerne: true } {
  return "nonConcerne" in cible;
}

/**
 * Écart d'un score à l'intervalle cible : 0 si dedans, sinon la distance à la
 * borne franchie.
 *
 * LE DÉPASSEMENT COMPTE : `s > max` produit un écart au même titre que
 * `s < min`. C'est le sens d'une fourchette plutôt que d'un plancher — sur une
 * dimension donnée, le fait qu'un candidat ait un score au-dessus de ce qui est
 * attendu pour le poste n'est pas plus positif que le cas inverse. À expliciter
 * dans l'UI de saisie, sinon les référents mettront `max = 10` par réflexe.
 */
export function ecartDimension(
  s: number,
  cible: { min: number; max: number },
): number {
  return Math.max(0, cible.min - s, s - cible.max);
}

/**
 * Pire écart possible sur une dimension, compte tenu de l'échelle. Sert à
 * repérer une dimension qui ne discrimine personne.
 *
 * Vaut 0 quand la cible couvre toute l'échelle : la dimension ne discrimine
 * rien et sort AUTOMATIQUEMENT du numérateur comme du dénominateur.
 */
export function ecartMaxDimension(
  cible: { min: number; max: number },
  echelle: Echelle,
): number {
  return Math.max(cible.min - echelle.min, echelle.max - cible.max);
}

/** Poids effectif d'une cible : 1 par défaut (attente unique), 1 / 0,5 pour
 *  les contributions historiques à importance. 0 = hors calcul. */
export function poidsDeCible(cible: CibleFourchette): number {
  if (estNonConcerne(cible)) return 0;
  return cible.poids ?? POIDS_PAR_DEFAUT;
}

/** Largeur de la fourchette — proxy inverse de l'exigence. */
export function largeur(cible: { min: number; max: number }): number {
  return cible.max - cible.min;
}

/**
 * Niveau d'exigence déduit de la largeur, relatif à l'amplitude de l'échelle.
 * Libellé destiné à l'UI de saisie, pour que le référent VOIE l'effet de son
 * réglage au moment où il le fait.
 */
export function exigence(
  cible: { min: number; max: number },
  echelle: Echelle,
): "forte" | "moderee" | "faible" | "indifferent" {
  const amplitude = echelle.max - echelle.min;
  const l = largeur(cible);
  if (l >= amplitude) return "indifferent";
  if (l <= amplitude * 0.3) return "forte";
  if (l <= amplitude * 0.6) return "moderee";
  return "faible";
}

/**
 * Largeur σ de la courbe de perte, en fraction de l'amplitude : σ = 2 points
 * sur l'échelle 0-10. Exprimée en fraction pour rester correcte si l'échelle
 * change, comme `intervalleDuNiveau`. Cf. docs/SCORE_MATCHING_PAMI.md.
 */
export const SIGMA_PERTE = 0.2;

/**
 * Fonction d'erreur de Gauss (Abramowitz & Stegun 7.1.26, erreur < 1,5·10⁻⁷).
 * `Math` n'en fournit pas.
 */
export function erf(x: number): number {
  // Exact en 0 : l'approximation y laisse ~10⁻⁹, et « dans la zone » doit
  // valoir 100 tout rond.
  if (x === 0) return 0;
  const signe = x < 0 ? -1 : 1;
  const a = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * a);
  const polynome =
    t *
    (0.254829592 +
      t * (-0.284496736 + t * (1.421413741 + t * (-1.453152027 + t * 1.061405429))));
  return signe * (1 - polynome * Math.exp(-a * a));
}

/**
 * Part de la dimension PERDUE, entre 0 et 1, en fonction de l'écart à la zone.
 *
 * La perte par point d'écart suit une GAUSSIENNE partant de la borne : le
 * premier point hors zone coûte le plus, chaque point suivant coûte moins. La
 * perte cumulée est donc l'aire sous une demi-cloche, `erf(écart / σ√2)`.
 * Avec σ = 2 : 1 point → 38 %, 2 → 68 %, 3 → 87 %, 4 → 95 %, 5 → 99 %. Elle
 * tend vers 100 % sans l'atteindre : aucune coupure arbitraire.
 */
export function perteDimension(
  s: number,
  cible: { min: number; max: number },
  echelle: Echelle,
): number {
  const sigma = SIGMA_PERTE * (echelle.max - echelle.min);
  return erf(ecartDimension(s, cible) / (sigma * Math.SQRT2));
}

/** Satisfaction d'une dimension, entre 0 et 1 : `1 − perte`. Vaut 1 dans la
 *  zone. */
export function satisfactionDimension(
  s: number,
  cible: { min: number; max: number },
  echelle: Echelle,
): number {
  return 1 - perteDimension(s, cible, echelle);
}

/**
 * Score de matching sur 100 : moyenne des satisfactions par dimension,
 *
 *     100 × Σ poids·sat / Σ poids,   sat = 1 − erf(écart / σ√2)
 *
 * Les contributions à attente unique pèsent toutes 1 : c'est une moyenne
 * simple. Seules les contributions historiques à importance portent un poids
 * de 0,5 (« important »).
 *
 * L'écart est la distance à la BORNE LA PLUS PROCHE de la zone attendue
 * (`ecartDimension`) : un candidat dans la zone obtient tous les points de la
 * dimension, où qu'il s'y trouve.
 *
 * Sortent du calcul — ni au numérateur ni au dénominateur :
 * - une dimension sans attente particulière (`nonConcerne`) ou historiquement
 *   « pas importante » (poids 0) : aucune zone à comparer ;
 * - les dimensions `nonConcerne`, ou dont la cible couvre toute l'échelle
 *   (contributions historiques), qui ne départagent personne ;
 * - une dimension sans score chez le candidat.
 *
 * Si AUCUNE dimension ne pèse, le score n'est pas défini : renvoie `null`, à
 * afficher comme un message explicite — surtout pas 100 ni 0.
 */
export function scoreMatchingFourchette(
  scoresCandidat: Record<string, number>,
  cibles: Record<string, CibleFourchette>,
  echelle: Echelle,
): { score: number; dimensionsRetenues: string[] } | null {
  const amplitude = echelle.max - echelle.min;
  let satisfactionPonderee = 0;
  let sommePoids = 0;
  const dimensionsRetenues: string[] = [];

  for (const [code, cible] of Object.entries(cibles)) {
    if (estNonConcerne(cible)) continue;
    if (ecartMaxDimension(cible, echelle) === 0) continue; // non discriminante
    const poids = poidsDeCible(cible);
    if (poids === 0) continue; // historique « pas important » : aucune zone
    const s = scoresCandidat[code];
    if (s === undefined) continue; // dimension non mesurée chez ce candidat
    satisfactionPonderee += poids * satisfactionDimension(s, cible, echelle);
    sommePoids += poids;
    dimensionsRetenues.push(code);
  }

  if (sommePoids === 0 || amplitude === 0) return null;
  const score = (100 * satisfactionPonderee) / sommePoids;
  return { score, dimensionsRetenues };
}
